const BYTES_PER_MB = 1048576

/** Last extension in lower case with a leading dot ('.xlsx'); '' when there is no dot. */
export const getFileExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex <= 0) return ''
  return fileName.slice(dotIndex).toLowerCase()
}

/** Accept entries may come with or without a leading dot, in any letter case. */
export const isExtensionAllowed = (fileName: string, accept: string[]): boolean => {
  const extension = getFileExtension(fileName)
  if (!extension) return false
  return accept.some((entry) => {
    const normalized = entry.toLowerCase()
    return (normalized.startsWith('.') ? normalized : `.${normalized}`) === extension
  })
}

export const isSizeAllowed = (sizeBytes: number, maxSizeMb: number): boolean =>
  sizeBytes <= maxSizeMb * BYTES_PER_MB

export const formatFileSize = (bytes: number): string => {
  if (bytes >= BYTES_PER_MB) return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** '412 שורות · 24 KB'; only the size when rowCount is null (no dangling separator). */
export const formatStagedMeta = (rowCount: number | null, sizeBytes: number): string => {
  const size = formatFileSize(sizeBytes)
  if (rowCount === null) return size
  return `${rowCount} שורות · ${size}`
}

/**
 * Settles no earlier than minMs after the call — keeps the upload animation
 * visible even when the server answers instantly. Value and rejection reason
 * pass through unchanged; a task slower than minMs is never cut short.
 */
export const withMinDuration = <T>(task: Promise<T>, minMs: number): Promise<T> => {
  const minDelay = new Promise<void>((resolve) => {
    setTimeout(resolve, minMs)
  })
  return Promise.allSettled([task, minDelay]).then(([taskResult]) => {
    if (taskResult.status === 'rejected') throw taskResult.reason
    return taskResult.value
  })
}
